import { Building } from '@carbon/react/icons';
import type {
  PartyLink,
  PartySource,
  PartySummary,
  PartySourceBlock,
} from '../types';
import { PartyCellList } from './expand/common';
import { NOT_AVAILABLE } from './utils';
import { ReviewStatusIcon } from './review-status-icon';
import styles from './document-card.module.scss';

// Parties shown before collapsing into "+N more" on the collapsed card.
const PARTY_VISIBLE = 2;

/**
 * Review-state indicator, mirroring the Global Interpretations review-count
 * icons: a green check when Entity Review shows every party resolved
 * (Structured), a blue in-progress circle when only some are (Structuring).
 * @param {object} props The indicator.
 * @param {PartySource} props.source The parties' source.
 * @return {JSX.Element | null} The indicator.
 */
function SourceIndicator({ source }: { source: PartySource }) {
  if (source !== 'structured' && source !== 'structuring') return null;
  return (
    <ReviewStatusIcon
      status={source === 'structured' ? 'reviewed' : 'partial'}
      className={styles.sourceGlyph}
      showPending={false}
      partialLabel="In review"
    />
  );
}

/**
 * One labeled role row with its first parties and a "Show more" overflow.
 * @param {object} props The row.
 * @param {string} props.label The role label.
 * @param {PartyLink[]} props.parties The role's parties.
 * @param {Function} [props.onShowMore] Called on the overflow click.
 * @return {JSX.Element} The row.
 */
function SummaryRow({
  label,
  parties,
  onShowMore,
}: {
  label: string;
  parties: PartyLink[];
  onShowMore?: () => void;
}) {
  const count = parties.length;
  const rowLabel = `${count === 1 ? label : `${label}s`} (${count})`;
  return (
    <div className={styles.afePartyRow}>
      <Building className={styles.afePartyIcon} />
      <span className={styles.afePartyLabel}>{rowLabel}</span>
      <span className={styles.afePartyValue}>
        <PartyCellList
          parties={parties}
          max={PARTY_VISIBLE}
          onShowMore={onShowMore}
        />
      </span>
    </div>
  );
}

/**
 * The collapsed card's party block for one source: the "Source:" label + review
 * indicator, then its labeled role rows. Accepts either the non-typed
 * grantor/grantee summary or a typed source block with explicit `rows`.
 * @param {object} props The block.
 * @param {PartySummary | PartySourceBlock} props.summary The source's parties.
 * @param {Function} [props.onShowMore] Called on a row's overflow click.
 * @return {JSX.Element} The block.
 */
export function PartySummaryBlock({
  summary,
  onShowMore,
}: {
  summary: PartySummary | PartySourceBlock;
  // Called when a role row's "Show more +(N)" overflow is clicked; expands the
  // card and opens the Parties accordion.
  onShowMore?: () => void;
}) {
  const rows =
    'rows' in summary
      ? summary.rows
      : [
          { label: 'Conveyor', parties: summary.grantors },
          { label: 'Conveyee', parties: summary.grantees },
          ...(summary.others.length
            ? [{ label: 'Other', parties: summary.others }]
            : []),
        ];
  if (!summary.source) {
    return (
      <div className={styles.afeParties}>
        <div className={styles.afePartiesHeader}>
          Source: <span className={styles.muted}>{NOT_AVAILABLE}</span>
        </div>
      </div>
    );
  }
  const hasParties = rows.some((row) => row.parties.length > 0);
  return (
    <div className={styles.afeParties}>
      <div className={styles.afePartiesHeader}>
        {`Source: ${summary.sourceLabel}`}
        <SourceIndicator source={summary.source} />
      </div>
      {hasParties ? (
        rows.map((row) => (
          <SummaryRow
            key={row.label}
            label={row.label}
            parties={row.parties}
            onShowMore={onShowMore}
          />
        ))
      ) : (
        <div className={styles.sourceEmpty}>{NOT_AVAILABLE}</div>
      )}
    </div>
  );
}
